<template>
  <Modal
    :model-value="modelValue"
    content-class="!max-w-2xl w-full"
    :show-close="true"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="flex flex-col gap-4">
      <h2 class="text-xl font-semibold">{{ t('pickPayment') }}</h2>

      <div
        v-if="contextReferences?.length"
        class="rounded-md border border-obsidian-100 bg-obsidian-50 p-3"
      >
        <p class="mb-2 text-xs font-medium text-obsidian-500">{{ t('detailReferences') }}</p>
        <div class="flex flex-col gap-1.5 font-mono text-xs leading-snug text-obsidian-800">
          <div
            v-for="contextRef in contextReferences"
            :key="contextRef.label + contextRef.value"
            class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
          >
            <span class="shrink-0 text-obsidian-400">{{ contextRef.label }}</span>
            <span class="select-all break-all">{{ contextRef.value }}</span>
          </div>
        </div>
      </div>

      <Input
        :model-value="search"
        :placeholder="t('searchPlaceholder')"
        variant="ghost"
        @update:model-value="emit('update:search', String($event ?? ''))"
      />

      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner class="text-hazel-600" />
      </div>

      <EntityListExceptions
        v-else
        :status="status"
        :error="error as any"
        :items-count="items.length"
        :no-items-text="search ? t('noSearchResults') : t('noSuggestions')"
        small
      />

      <div v-if="items.length" class="flex max-h-96 flex-col gap-2 overflow-y-auto">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="rounded-md border p-3 text-left transition"
          :class="
            selectedId === item.id
              ? 'border-bittersweet-300 bg-bittersweet-50'
              : 'border-obsidian-100 bg-white hover:border-obsidian-200'
          "
          @click="selectedId = item.id"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="font-medium text-obsidian-900">
                {{
                  item.bookingNumber
                    ? t('bookingLabel', { number: item.bookingNumber })
                    : t('noBooking')
                }}
                <span
                  v-if="markAsSuggestions"
                  class="ml-2 text-sm font-normal text-bittersweet-700"
                >
                  {{ t('suggested') }}
                </span>
              </p>
              <p class="mt-1 flex flex-wrap items-center gap-2 text-sm text-obsidian-500">
                <span>{{ formatMeta(item) }}</span>
                <CounterpartyTypeBadge
                  v-if="item.counterpartyType"
                  :counterparty-type="item.counterpartyType"
                  size="sm"
                />
              </p>
              <p v-if="item.referenceId" class="mt-0.5 font-mono text-xs text-obsidian-400">
                {{ item.referenceId }}
              </p>
            </div>
            <p class="flex-shrink-0 font-semibold tabular-nums">
              {{ formatCurrency(item.amount) }}
            </p>
          </div>
        </button>

        <div v-if="hasNextPage" class="flex justify-center pt-1">
          <CVButton outline size="sm" :is-loading="isFetchingNextPage" @click="emit('loadMore')">
            {{ t('loadMore') }}
          </CVButton>
        </div>
      </div>

      <div class="flex justify-end gap-3 border-t border-obsidian-100 pt-4">
        <CVButton variant="secondary" @click="handleCancel">{{ t('cancel') }}</CVButton>
        <CVButton
          variant="default"
          :disabled="!selectedId"
          :is-loading="isConfirming"
          @click="handleConfirm"
        >
          {{ t('select') }}
        </CVButton>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/components/Modal.vue';
import Input from '@/components/Input.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import CounterpartyTypeBadge from '@/components/badges/CounterpartyTypeBadge.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { formatDate } from '@/hooks/use-formatted-date';
import { DateTime } from 'luxon';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CounterpartyType, PaymentType } from '@carvia/ros-client-types';

export type PaymentPickerContextReference = {
  label: string;
  value: string;
};

export type PaymentPickerItem = {
  id: string;
  amount: number;
  date?: string | Date;
  paidDate?: string | Date | null;
  counterpartyType?: CounterpartyType | null;
  paymentType?: PaymentType;
  referenceId?: string | null;
  bookingNumber?: number | null;
};

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    search: string;
    items: PaymentPickerItem[];
    isLoading?: boolean;
    status?: 'pending' | 'error' | 'success';
    error?: unknown;
    isConfirming?: boolean;
    contextReferences?: PaymentPickerContextReference[];
    /** When true (no active search), show suggestion labels on all rows. */
    markAsSuggestions?: boolean;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
  }>(),
  {
    isLoading: false,
    status: 'pending',
    error: undefined,
    contextReferences: () => [],
    markAsSuggestions: false,
    hasNextPage: false,
    isFetchingNextPage: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:search', value: string): void;
  (e: 'select', payment: PaymentPickerItem): void;
  (e: 'loadMore'): void;
}>();

const { t } = useI18n();
const selectedId = ref<string | null>(null);

const formatMeta = (item: PaymentPickerItem) => {
  const parts: string[] = [];
  const date = item.paidDate ?? item.date;
  if (date) parts.push(formatDate(date, DateTime.DATE_SHORT));
  if (item.paymentType) parts.push(t(`paymentTypes.${item.paymentType}`));
  return parts.join(', ') || t('noReference');
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) selectedId.value = null;
  },
);

const handleCancel = () => emit('update:modelValue', false);

const handleConfirm = () => {
  const payment = props.items.find((item) => item.id === selectedId.value);
  if (!payment) return;
  emit('select', payment);
};
</script>

<i18n lang="json">
{
  "en": {
    "pickPayment": "Select payment",
    "detailReferences": "Settlement detail references",
    "searchPlaceholder": "Search booking number or reference…",
    "noSuggestions": "No suggested payments",
    "noSearchResults": "No payments found",
    "bookingLabel": "Booking {number}",
    "noBooking": "No booking",
    "noReference": "No reference",
    "suggested": "Suggested",
    "loadMore": "Load more",
    "cancel": "Cancel",
    "select": "Select"
  },
  "de": {
    "pickPayment": "Zahlung auswählen",
    "detailReferences": "Referenzen der Settlement-Zeile",
    "searchPlaceholder": "Buchungsnummer oder Referenz suchen…",
    "noSuggestions": "Keine Zahlungsvorschläge",
    "noSearchResults": "Keine Zahlungen gefunden",
    "bookingLabel": "Buchung {number}",
    "noBooking": "Keine Buchung",
    "noReference": "Keine Referenz",
    "suggested": "Vorschlag",
    "loadMore": "Mehr laden",
    "cancel": "Abbrechen",
    "select": "Auswählen"
  },
  "es": {
    "pickPayment": "Seleccionar pago",
    "detailReferences": "Referencias de la línea de liquidación",
    "searchPlaceholder": "Buscar número de reserva o referencia…",
    "noSuggestions": "No hay pagos sugeridos",
    "noSearchResults": "No se encontraron pagos",
    "bookingLabel": "Reserva {number}",
    "noBooking": "Sin reserva",
    "noReference": "Sin referencia",
    "suggested": "Sugerido",
    "loadMore": "Cargar más",
    "cancel": "Cancelar",
    "select": "Seleccionar"
  }
}
</i18n>
